import { map } from 'rxjs';
import { Observable, Subscription } from 'rxjs';
import { SyncEvent, SyncEventStorage, SyncStorage } from './sync.model';
import { SyncPartition } from './sync.partition';
import { SyncService } from './sync.service';

export type PartitionValue = string[] | string | null;

export abstract class SyncModule<T extends { id?: string }> {
    static readonly modules: Array<SyncModule<{ id?: string }>> = [];

    private subscription?: Subscription;
    private readonly partitions: { [topic: string]: SyncPartition<T> } = {};

    protected get syncService() {
        return SyncService.instance;
    }

    protected abstract storage: SyncStorage<T>;
    protected abstract eventStorage: SyncEventStorage<T> | null | undefined;

    private loading = 0;

    /**
     * @param delayGapRepair wait for late events before repairing a sequence gap; for shared broadcast stores only.
     */
    constructor(private readonly lazy: boolean, private readonly simulcast = false, private readonly delayGapRepair = false) {
        SyncModule.modules.push(this);
    }

    stop() {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
    }

    /**
     * Closes every partition and writes the stored rows to disk. With `reset` it also removes every partition's sequence
     * and writes that to disk before resolving, so a reload right after sign-out never resumes from a cleared partition's sequence.
     */
    async close(reset: boolean) {
        await Promise.all(Object.keys(this.partitions).map(async removed => {
            await this.partitions[removed].close(reset);
            delete this.partitions[removed];
        }));
        if (reset) {
            const remaining = await this.eventStorage?.getPartitions();
            await Promise.all((remaining ?? []).map(async (removed) => await this.eventStorage?.close(removed)));
            await this.eventStorage?.flush();
        }
        await this.storage.flush();
        this.storage.clearCache?.();
        this.addLoading(-this.loading);
    }

    /** Catches every partition up after MQTT moved to another broker, resolving to how many ran and how many failed. */
    async catchUp(movedAt: number): Promise<{ partitions: number, failed: number }> {
        const results = (await Promise.all(Object.values(this.partitions).map(partition => partition.catchUp(movedAt))))
            .filter(result => result !== undefined);
        return { partitions: results.length, failed: results.filter(result => !result).length };
    }

    async flush() {
        await Promise.all(Object.values(this.partitions).map(async partition => {
            await partition.flush();
        }));
    }

    protected watchStreams(streams: Observable<PartitionValue>): Observable<PartitionValue> {
        return streams.pipe(map(value => value));
    }

    // Usually userId or accountIds
    abstract watchPartitions(): Observable<string[] | string | null>;
    abstract getTopic(topic: string): string;
    abstract getName(): string;

    start() {
        this.subscription = this.watchPartitions().subscribe({
            // eslint-disable-next-line @typescript-eslint/no-misused-promises, rxjs-x/no-async-subscribe
            next: async (partitionOrList) => {
                // On startup we might not have the right set of topics, let it get right
                // Not doing this can cause a stale startup state
                if (!partitionOrList) return;

                this.addLoading(1);
                const partitions = Array.isArray(partitionOrList) ? partitionOrList : [partitionOrList];

                // If we started a partition, stop it
                Object.keys(this.partitions)
                    .filter(x => !partitions.includes(x))
                    .forEach(removed => {
                        void this.partitions[removed].close(true);
                        delete this.partitions[removed];
                    });

                // If we have a lingering partition from before startup, remove it
                const previousPartitions = await this.eventStorage?.getPartitions();
                await Promise.all(
                    (previousPartitions ?? [])
                        .filter(x => !partitions.includes(x))
                        .map(async (removed) => await this.eventStorage?.close(removed)),
                );

                // New topic, add and start it
                partitions.filter(partition => !this.partitions[partition]).forEach(partition => this.addPartition(partition));
                this.addLoading(-1);
            }, error: (error: unknown) => {
                console.error(error);
            },
        });
    }

    private addPartition(partition: string, reset = false) {
        if (!this.syncService) {
            console.warn('SyncService not initialized!');
            return;
        }

        const topic = this.getTopic(partition);
        this.partitions[partition] = new SyncPartition(this.syncService, this.storage, topic, partition, this.lazy, this.simulcast, reset, this.eventStorage, this.delayGapRepair).start();

        if (!reset) {
            this.addLoading(1);
            let loaded = false;
            this.partitions[partition].loaded$.subscribe(() => {
                if (!loaded) this.addLoading(-1);
                loaded = true;
            });
        }
        this.partitions[partition].reset$.subscribe(() => {
            delete this.partitions[partition];
            this.addPartition(partition, true);
        });
    }

    async applySync(partition: string, record: SyncEvent<T>) {
        await this.partitions[partition]?.queueUpdate([record], false);
    }

    private addLoading(change: number) {
        if (!this.syncService) return;

        this.syncService.changeLoadingCount(change, this.getName());
        const subj = this.lazy ? this.syncService.lazyLoading$ : this.syncService.priorityLoading$;
        subj.next(subj.value + change);
        this.loading += change;
    }
}
